// src/services/googleAuth.js

class GoogleAuthService {
  constructor() {
    this.isInitialized = false;
    this.google = null;
  }

  // Check if Google Client ID is configured
  getClientId() {
    const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
    if (!clientId) {
      console.error('Google Client ID not found');
      return null;
    }
    return clientId;
  }

  // Initialize Google Identity Services
  async init() {
    if (this.isInitialized) return true;

    try {
      const clientId = this.getClientId();
      if (!clientId) {
        throw new Error('Google Client ID is not configured');
      }

      // Load Google Identity Services script
      await this.loadGoogleIdentityScript();
      
      // Wait for google object to be available
      await this.waitForGoogle();

      this.isInitialized = true;
      return true;
    } catch (error) {
      console.error('Failed to initialize:', error);
      return false;
    }
  }

  // Load Google Identity Services script
  loadGoogleIdentityScript() {
    return new Promise((resolve, reject) => {
      if (window.google?.accounts?.oauth2) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = resolve;
      script.onerror = () => reject(new Error('Failed to load Google script'));
      document.head.appendChild(script);
    });
  }

  // Wait for Google object to be available
  waitForGoogle() {
    return new Promise((resolve, reject) => {
      let attempts = 0;
      const maxAttempts = 50;
      
      const checkGoogle = () => {
        if (window.google?.accounts?.oauth2) {
          resolve();
        } else if (attempts < maxAttempts) {
          attempts++;
          setTimeout(checkGoogle, 100);
        } else {
          reject(new Error('Google OAuth2 API failed to load'));
        }
      };
      
      checkGoogle();
    });
  }

  // Sign in with Google
  async signIn() {
    try {
      if (!this.isInitialized) {
        const initialized = await this.init();
        if (!initialized) {
          throw new Error('Failed to initialize Google Identity Services');
        }
      }

      // Use the Authorization Code flow with popup
      return new Promise((resolve, reject) => {
        let isCallbackCalled = false;
        let timeoutId = null;
        
        const client = window.google.accounts.oauth2.initCodeClient({
          client_id: this.getClientId(),
          scope: 'email profile openid',
          ux_mode: 'popup',
          callback: (response) => {
            isCallbackCalled = true;
            if (timeoutId) clearTimeout(timeoutId);
            
            if (response.error) {
              reject(new Error(response.error));
              return;
            }
            console.log('Authorization Code:', response.code);
            resolve({ success: true, code: response.code });
          },
          error_callback: (error) => {
            isCallbackCalled = true;
            if (timeoutId) clearTimeout(timeoutId);
            reject(new Error(error.type || 'Authentication failed'));
          }
        });

        // Request authorization code
        try {
          console.log('🚀 Requesting authorization code from Google');
          client.requestCode();
          
          // Set a timeout to detect popup closure
          // Use a shorter timeout to quickly detect popup closure
          timeoutId = setTimeout(() => {
            if (!isCallbackCalled) {
              console.log('⏰ Authentication popup timeout - likely closed without completing');
              reject(new Error('popup_closed'));
            }
          }, 10000); // 10 second timeout to quickly detect popup closure
          
          // Additional immediate check for popup blocker
          setTimeout(() => {
            if (!isCallbackCalled) {
              console.log('🔍 Quick check - popup might be blocked or closed immediately');
            }
          }, 1000);
          
        } catch (error) {
          console.error('❌ Failed to request authorization code:', error);
          reject(new Error('Failed to open authentication popup'));
        }
      });

    } catch (error) {
      console.error('Sign-in failed:', error);
      return { success: false, error: error.message };
    }
  }

}

// Create singleton instance
const googleAuthService = new GoogleAuthService();

export default googleAuthService;